import { create } from 'zustand';
import {
  getTeamMembers,
  getCourses,
  getLearningHistory,
  getAdminActivities,
  getTeamStats,
  getAllTeams,
  getTeamById,
  createTeam,
  setTeamManager,
  updateTeam,
  deleteTeam,
  getAvailableUsers,
  addTeamMembers,
  removeTeamMember,
  assignCoursesToTeam,
  bulkEnrollUsers,
  TeamMember,
  Course,
  LearningHistoryItem,
  AdminActivity,
  TeamStats,
  Team,
  AvailableUser,
} from '@/services/PagesServices/MyTeamService';

interface MyTeamState {
  // Data
  teamMembers: TeamMember[] | null;
  courses: Course[] | null;
  learningHistory: LearningHistoryItem[] | null;
  adminActivities: AdminActivity[] | null;
  teamStats: TeamStats | null;
  teams: Team[] | null;
  selectedTeam: Team | null;
  availableUsers: AvailableUser[] | null;

  // UI State
  loading: boolean;
  error: string | null;

  // Actions - Fetch Data
  fetchTeamMembers: (teamId?: number) => Promise<void>;
  fetchCourses: () => Promise<void>;
  fetchLearningHistory: (teamId?: number, userId?: string, status?: string) => Promise<void>;
  fetchAdminActivities: (userId?: string) => Promise<void>;
  fetchTeamStats: () => Promise<void>;

  // Actions - Team CRUD
  fetchAllTeams: () => Promise<void>;
  fetchTeamById: (teamId: number) => Promise<void>;
  createNewTeam: (name: string, description: string, organizationId?: number | null, managerId?: string | null) => Promise<{ success: boolean; id?: number }>;
  setTeamManagerById: (teamId: number, managerId: string | null) => Promise<boolean>;
  updateExistingTeam: (teamId: number, name: string, description: string) => Promise<boolean>;
  deleteExistingTeam: (teamId: number) => Promise<boolean>;

  // Actions - Team Members
  fetchAvailableUsers: (excludeTeamId?: number) => Promise<void>;
  addMembersToTeam: (teamId: number, userIds: string[]) => Promise<{ success: boolean; addedCount?: number }>;
  removeMemberFromTeam: (teamId: number, userId: string) => Promise<boolean>;

  // Actions - Course Assignment
  assignCourses: (teamId: number, courses: Array<{ courseId: number; deadline?: string }>) => Promise<{ success: boolean; data?: { assignedCourses: number; enrolledUsers: number } }>;
  bulkEnroll: (userIds: string[], courseIds: string[], deadline?: string) => Promise<{ success: boolean; data?: { enrolledCount: number; userCount: number; courseCount: number } }>;

  // Actions - Clear/Reset
  clearError: () => void;
  setSelectedTeam: (team: Team | null) => void;
}

const useMyTeamStore = create<MyTeamState>((set, get) => ({
  // Initial State
  teamMembers: null,
  courses: null,
  learningHistory: null,
  adminActivities: null,
  teamStats: null,
  teams: null,
  selectedTeam: null,
  availableUsers: null,
  loading: false,
  error: null,

  // Fetch Team Members
  fetchTeamMembers: async (teamId?: number) => {
    try {
      set({ loading: true, error: null });
      const response = await getTeamMembers(teamId);
      if (response.success) {
        set({ teamMembers: response.data || [], loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch team members', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch Courses
  fetchCourses: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getCourses();
      if (response.success) {
        set({ courses: response.data || [], loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch courses', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch Learning History
  fetchLearningHistory: async (teamId?: number, userId?: string, status?: string) => {
    try {
      set({ loading: true, error: null });
      const response = await getLearningHistory(teamId, userId, status);
      if (response.success) {
        set({ learningHistory: response.data || [], loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch learning history', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch Admin Activities
  fetchAdminActivities: async (userId?: string) => {
    try {
      set({ loading: true, error: null });
      const response = await getAdminActivities(userId);
      if (response.success) {
        set({ adminActivities: response.data || [], loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch admin activities', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch Team Stats
  fetchTeamStats: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getTeamStats();
      if (response.success) {
        set({ teamStats: response.data || null, loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch team stats', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch All Teams
  fetchAllTeams: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getAllTeams();
      if (response.success) {
        set({ teams: response.data || [], loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch teams', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Fetch Team By ID
  fetchTeamById: async (teamId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await getTeamById(teamId);
      if (response.success && response.data) {
        set({
          selectedTeam: response.data.team,
          teamMembers: response.data.members,
          loading: false
        });
      } else {
        set({ error: response.error?.message || 'Failed to fetch team', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Create New Team
  createNewTeam: async (
    name: string,
    description: string,
    organizationId?: number | null,
    managerId?: string | null
  ) => {
    try {
      set({ loading: true, error: null });
      const response = await createTeam(name, description, organizationId ?? null, managerId ?? null);
      set({ loading: false });
      if (response.success && response.data) {
        // Refresh teams list
        get().fetchAllTeams();
        return { success: true, id: response.data.id };
      } else {
        set({ error: response.error?.message || 'Failed to create team' });
        return { success: false };
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false };
    }
  },

  setTeamManagerById: async (teamId: number, managerId: string | null) => {
    try {
      set({ loading: true, error: null });
      const response = await setTeamManager(teamId, managerId);
      set({ loading: false });
      if (response.success) {
        get().fetchAllTeams();
        return true;
      }
      set({ error: response.error?.message || 'Failed to set team manager' });
      return false;
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Update Existing Team
  updateExistingTeam: async (teamId: number, name: string, description: string) => {
    try {
      set({ loading: true, error: null });
      const response = await updateTeam(teamId, name, description);
      set({ loading: false });
      if (response.success) {
        // Refresh teams list
        get().fetchAllTeams();
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to update team' });
        return false;
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Delete Existing Team
  deleteExistingTeam: async (teamId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await deleteTeam(teamId);
      set({ loading: false });
      if (response.success) {
        // Refresh teams list
        get().fetchAllTeams();
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to delete team' });
        return false;
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Fetch Available Users
  fetchAvailableUsers: async (excludeTeamId?: number) => {
    try {
      set({ loading: true, error: null });
      const response = await getAvailableUsers(excludeTeamId);
      if (response.success) {
        set({ availableUsers: response.data || [], loading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch available users', loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  // Add Members to Team
  addMembersToTeam: async (teamId: number, userIds: string[]) => {
    try {
      set({ loading: true, error: null });
      const response = await addTeamMembers(teamId, userIds);
      set({ loading: false });
      if (response.success && response.data) {
        // Refresh team members
        get().fetchTeamMembers(teamId);
        get().fetchAvailableUsers();
        return { success: true, addedCount: response.data.addedCount };
      } else {
        set({ error: response.error?.message || 'Failed to add members' });
        return { success: false };
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false };
    }
  },

  // Remove Member from Team
  removeMemberFromTeam: async (teamId: number, userId: string) => {
    try {
      set({ loading: true, error: null });
      const response = await removeTeamMember(teamId, userId);
      set({ loading: false });
      if (response.success) {
        // Refresh team members
        get().fetchTeamMembers(teamId);
        get().fetchAvailableUsers();
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to remove member' });
        return false;
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return false;
    }
  },

  // Assign Courses to Team
  assignCourses: async (teamId: number, courses: Array<{ courseId: number; deadline?: string }>) => {
    try {
      set({ loading: true, error: null });
      const response = await assignCoursesToTeam(teamId, courses);
      set({ loading: false });
      if (response.success && response.data) {
        // Refresh learning history
        get().fetchLearningHistory(teamId);
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message || 'Failed to assign courses' });
        return { success: false };
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false };
    }
  },

  // Bulk Enroll Users in Courses
  bulkEnroll: async (userIds: string[], courseIds: string[], deadline?: string) => {
    try {
      set({ loading: true, error: null });
      const response = await bulkEnrollUsers(userIds, courseIds, deadline);
      set({ loading: false });
      if (response.success && response.data) {
        // Refresh data
        get().fetchLearningHistory();
        get().fetchTeamMembers();
        return { success: true, data: response.data };
      } else {
        set({ error: response.error?.message || 'Failed to enroll users' });
        return { success: false };
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return { success: false };
    }
  },

  // Clear Error
  clearError: () => {
    set({ error: null });
  },

  // Set Selected Team
  setSelectedTeam: (team: Team | null) => {
    set({ selectedTeam: team });
  },
}));

export default useMyTeamStore;
